import { useState } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import useProductCardStyles from "./useProductCardStyles";
import { useFavourite } from "../../../hooks/common-hooks/useFavourite";
import { selectorfavouriteProducts } from "../../../store/reducers/favourites";
import { ROUTE_PATH } from "../../../constants/route";
import { useShoppingCart } from "../../../context/ShoppingCartContext";
import { CustomizedShimmer } from "../Shimmer";
import FavSvg from "../../../assets/icons/newFavIcon.svg";
import FavSelectedSvg from "../../../assets/icons/fav-selected.svg";
import BagBrownSvg from "../../../assets/icons/bagBrown.svg";
import WanderobeLogoNoImagePlaceholder from "../../../assets/icons/WanderobeLogoNoImagePlaceholder.svg";

const ProductCard = ({
  onClick = () => { },
  webWidth = "0 1 calc(25% - 16px)",
  tabWidth = "0 1 calc(33.33% - 16px)",
  minHeight = false,
  index,
  item,
  isFavouriteIconVisible,
  height = "auto",
  mobileWidth = "0 1 calc(89% - 16px)",
  fullWidth = false,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const styles = useProductCardStyles({
    height,
    mobileWidth,
    webWidth,
    tabWidth,
    fullWidth,
  });
  const { setFavouriteProduct, removeFavProduct, loading } = useFavourite();
  const { favProducts } = useSelector(selectorfavouriteProducts);
  const navigate = useNavigate();
  const { openModal, isOpen } = useShoppingCart();

  // Get all images from variants
  const productImages = Array.isArray(item?.images) ? item.images : [];
  const variantImages = Array.isArray(item?.variants) 
    ? item?.variants?.flatMap(variant => {
        const variantImg = variant?.variant_img;
        if (Array.isArray(variantImg)) {
          return variantImg;
        } else if (variantImg) {
          return [variantImg];
        }
        return [];
      })
    : [];
  const uniqueImages = [...new Set([...productImages, ...variantImages])];
  const allImages = uniqueImages || [];
  const hasMultipleImages = allImages?.length > 1;

  const handleCardClick = () => {
    if (!isOpen && !isDragging) {
      const productSlug = item?.name?.toLowerCase()?.replace(/\s+/g, '-')?.replace(/[^a-z0-9-]/g, '');
      navigate(ROUTE_PATH.PRODUCT_DETAIL?.replace(":product", productSlug)?.replace(":id", item?.id));
    }
  };

  const handleMouseDown = (e) => {
    setDragStartX(e.clientX);
    setIsDragging(false);
  };

  const handleMouseMove = (e) => {
    if (dragStartX !== 0) {
      const dragDistance = Math.abs(e.clientX - dragStartX);
      if (dragDistance > 5) { // Threshold for drag detection
        setIsDragging(true);
      }
    }
  };

  const handleMouseUp = () => {
    setDragStartX(0);
    // Reset dragging state after a short delay to allow click to register
    setTimeout(() => setIsDragging(false), 100);
  };

  const handleTouchStart = (e) => {
    setDragStartX(e.touches[0].clientX);
    setIsDragging(false);
  };

  const handleTouchMove = (e) => {
    if (dragStartX !== 0) {
      const dragDistance = Math.abs(e.touches[0].clientX - dragStartX);
      if (dragDistance > 5) { // Threshold for drag detection
        setIsDragging(true);
      }
    }
  };

  const handleTouchEnd = () => {
    setDragStartX(0);
    // Reset dragging state after a short delay to allow click to register
    setTimeout(() => setIsDragging(false), 100);
  };

  const handleImageLoad = () => {
    setIsImageLoading(false);
  };

  const handleImageError = () => {
    setIsImageLoading(false);
  };

  const changeSlide = (newSlideIndex) => {
    setCurrentSlide(newSlideIndex);
  };

  const handlePrevSlide = (e) => {
    e.stopPropagation();
    // Only navigate if not at first slide
    if (currentSlide > 0) {
      const newSlide = currentSlide - 1;
      changeSlide(newSlide);
    }
  };

  const handleNextSlide = (e) => {
    e.stopPropagation();
    // Only navigate if not at last slide
    if (currentSlide < allImages.length - 1) {
      const newSlide = currentSlide + 1;
      changeSlide(newSlide);
    }
  };

  // const handleDotClick = (e, index) => {
  //   e.stopPropagation();
  //   changeSlide(index);
  // };

  return (
    <>
      <Box
        onClick={() => (item?.quanity === 0 ? () => { } : handleCardClick())}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        key={index}
        sx={styles.card}
      >
        {isFavouriteIconVisible && (
          <>
            {favProducts?.includes(item?.id) ? (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  removeFavProduct(item?.id).then(() => {
                    onClick();
                  });
                }}
                className='icon hover-download'
                sx={styles.favSelectedIcon}
                aria-label='Remove from favourite'
                disabled={loading}
              >
                <img src={FavSelectedSvg} alt='Fav icon' />
              </IconButton>
            ) : (
              <IconButton
                className='icon hover-download'
                disabled={loading}
                onClick={(e) => {
                  e.stopPropagation();
                  setFavouriteProduct(item?.id);
                }}
                sx={styles.favIcon}
                aria-label='Add to favorites'
              >
                <img src={FavSvg} alt='Fav icon' />
              </IconButton>
            )}
          </>
        )}

        {/* Image Slider Container */}
        <Box sx={styles.imageContainer}>
          {isImageLoading && <CustomizedShimmer />}
          {/* Render all images and show/hide with CSS */}
          {allImages?.length > 0 && allImages?.map((imageSrc, index) => (
            <Box
              key={index}
              component='img'
              src={imageSrc}
              loading='lazy'
              alt={item?.name}
              sx={{
                ...(minHeight ? styles.image1 : styles.image),
                position: index === 0 ? 'relative' : 'absolute',
                top: index === 0 ? 'auto' : 0,
                left: index === 0 ? 'auto' : 0,
                opacity: index === currentSlide ? 1 : 0,
                transition: 'opacity 0.3s ease',
                zIndex: index === currentSlide ? 1 : 0,
              }}
              onLoad={index === 0 ? handleImageLoad : undefined}
              onError={index === 0 ? handleImageError : undefined}
            />
          ))}
          {/* Fallback image if no images in allImages array */}
          {allImages?.length === 0 && (
            <Box
              component='img'
              src={WanderobeLogoNoImagePlaceholder}
              loading='lazy'
              alt={item?.name}
              sx={{
                ...(minHeight ? styles.image1 : styles.image),
                opacity: isImageLoading ? 0 : 0.3,
                transition: 'opacity 0.3s ease',
                objectFit: 'contain',
              }}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
          )}

          {/* Arrow Buttons */}
          {hasMultipleImages && (
            <>
              <IconButton
                onClick={handlePrevSlide}
                sx={{
                  ...styles.arrowButton,
                  opacity: currentSlide === 0 ? 0.5 : 1,
                }}
                className="arrow-left"
              >
                <ChevronLeftIcon sx={styles.arrowIcon} />
              </IconButton>

              <IconButton
                onClick={handleNextSlide}
                sx={{
                  ...styles.arrowButton,
                  opacity: currentSlide === allImages.length - 1 ? 0.5 : 1,
                }}
                className="arrow-right"
              >
                <ChevronRightIcon sx={styles.arrowIcon} />
              </IconButton>
            </>
          )}
        </Box>

        <Box>
          <Box sx={styles.detailsWrapper}>
            {item?.quantity === 0 ? (
              <Typography sx={styles.outOfStock}>OUT OF STOCK</Typography>
            ) : (
              ""
            )}
            <Typography sx={styles.subtitle}>
              {item?.designer?.name || item?.seller_name}
            </Typography>
            <Typography sx={styles.title}>{item?.name}</Typography>
          </Box>
          <Box sx={styles.footer}>
            <Typography sx={styles.price}>${item?.price}</Typography>
            {item?.quantity === 0 ? (
              ""
            ) : (
              <IconButton
                onClick={(e) => {

                  e.stopPropagation();
                  openModal(item);
                }}
                className='hover-download'
                sx={styles.iconBackground}
                aria-label='Add to cart'
              >
                <Box component="img" sx={{ height: "24px", width: "24px" }} src={BagBrownSvg} alt="bag" />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default ProductCard;
